import { FC } from "react";
import { useAuthContext } from "../context/authContext";
import useI18n from "../hooks/useTranslations";
import SvgAlignVerticalBottom from "./icons/AlignVerticalBottom";
import SvgBorderAll from "./icons/BorderAll";
import SvgConversionPath from "./icons/ConversionPath";
import SvgH from "./icons/H";
import SvgHome from "./icons/Home";
import SvgItems from "./icons/Items";
import SvgList from "./icons/List";
import SvgListCheck from "./icons/ListCheck";
import SvgPeople from "./icons/People";
import SvgShare from "./icons/Share";
import SvgTextSnippet from "./icons/TextSnippet";
import SvgViewList from "./icons/ViewList";
import SvgViewTimeline from "./icons/ViewTimeline";
import { VERTIGRIP } from "./systems/adminSystemSettings/AdminSystemSettings";
import Nav from "./ui/Nav";

type mainMenuProps = {
  onClose: () => void;
};

// Matches the list's own page and its detail route (e.g. "/projects/:projectId"),
// but not sibling routes such as "/projects/shared".
const matchesListSection =
  (base: string, excludedSegments: string[] = []) =>
  (pathname: string) => {
    if (pathname === base) return true;
    if (!pathname.startsWith(`${base}/`)) return false;
    const segments = pathname.slice(base.length + 1).split("/");
    return segments.length === 1 && !excludedSegments.includes(segments[0] ?? "");
  };

// Configurations can be reached nested under a project (e.g. "/projects/:projectId/configurations/:configurationId"),
// in which case the configurations menu entry should be active instead of the projects one.
const matchesNestedConfiguration = (projectsBase: string) => (pathname: string) =>
  new RegExp(`^${projectsBase}/[^/]+/configurations/[^/]+$`).test(pathname);

// Systems don't have their own menu entry, so treat them as part of Home.
const isHomeActive = (pathname: string) =>
  pathname === "/" || pathname === "/systems" || pathname.startsWith("/systems/");
const isProjectsActive = matchesListSection("/projects", ["shared"]);
const isConfigurationsActive = (pathname: string) =>
  matchesListSection("/configurations", ["shared"])(pathname) || matchesNestedConfiguration("/projects")(pathname);
const isAdminProjectsActive = matchesListSection("/admin/projects");
const isAdminConfigurationsActive = (pathname: string) =>
  matchesListSection("/admin/configurations")(pathname) || matchesNestedConfiguration("/admin/projects")(pathname);

// Matches any admin page for the given system id (settings and its nested sub-pages, and the system's edit page).
const isAdminSystemActive = (systemId: string) => (pathname: string) =>
  pathname === `/admin/systems/${systemId}` || pathname.startsWith(`/admin/systems/${systemId}/`);

// The "Systems" entry itself should only highlight for the list page, not for a system's sub-pages
// (those highlight their own Vertigrip/Patrol/Border entry instead).
const isAdminSystemsListActive = (pathname: string) => pathname === "/admin/systems";

const MainMenu: FC<mainMenuProps> = ({ onClose }) => {
  const { permissions } = useAuthContext();
  const i18n = useI18n();

  const hasAdminPermissions = !!(
    permissions.admin.canManageProjects ||
    permissions.admin.canManageSystems ||
    permissions.admin.canManageUsers ||
    permissions.admin.canManageItems ||
    permissions.admin.canManageClients
  );

  return (
    <Nav>
      <Nav.Group title={i18n.translation.common.myArea}>
        <Nav.Item
          text={i18n.translation.common.home}
          to="/"
          icon={SvgHome}
          onClick={onClose}
          activeMatch={isHomeActive}
        />
        <Nav.Item text={i18n.translation.configurations.plural} icon={SvgListCheck} storageKey="configurations">
          <Nav.Item
            text={i18n.translation.configurations.my}
            to="/configurations"
            icon={SvgListCheck}
            onClick={onClose}
            activeMatch={isConfigurationsActive}
          />
          <Nav.Item
            text={i18n.translation.common.shared}
            to="/configurations/shared"
            icon={SvgShare}
            onClick={onClose}
          />
        </Nav.Item>
        <Nav.Item text={i18n.translation.projects.plural} icon={SvgList} storageKey="projects">
          <Nav.Item
            text={i18n.translation.projects.my}
            to="/projects"
            icon={SvgList}
            onClick={onClose}
            activeMatch={isProjectsActive}
          />
          <Nav.Item text={i18n.translation.common.shared} to="/projects/shared" icon={SvgShare} onClick={onClose} />
        </Nav.Item>
        <Nav.Item text={i18n.translation.documents.plural} to="/documents" icon={SvgTextSnippet} onClick={onClose} />
      </Nav.Group>
      {hasAdminPermissions && (
        <Nav.Group title={i18n.translation.common.administration}>
          {!!permissions.admin.canManageProjects && (
            <>
              <Nav.Item
                text={i18n.translation.configurations.all}
                to="/admin/configurations"
                icon={SvgListCheck}
                onClick={onClose}
                activeMatch={isAdminConfigurationsActive}
              />
              <Nav.Item
                text={i18n.translation.projects.all}
                to="/admin/projects"
                icon={SvgList}
                onClick={onClose}
                activeMatch={isAdminProjectsActive}
              />
              <Nav.Item
                text={i18n.translation.documents.all}
                to="/admin/documents"
                icon={SvgTextSnippet}
                onClick={onClose}
              />
            </>
          )}
          {!!permissions.admin.canManageSystems && (
            <Nav.Item
              text={i18n.translation.systems.plural}
              to="/admin/systems"
              icon={SvgViewList}
              onClick={onClose}
              storageKey="systems"
              activeMatch={isAdminSystemsListActive}
            >
              <Nav.Item
                text={VERTIGRIP}
                to="vertigrip"
                icon={SvgAlignVerticalBottom}
                onClick={onClose}
                activeMatch={isAdminSystemActive("vertigrip")}
              />
              <Nav.Item
                text={"Patrol"}
                to="patrol"
                icon={SvgConversionPath}
                onClick={onClose}
                activeMatch={isAdminSystemActive("patrol")}
              />
              <Nav.Item
                text={"Border"}
                to="border"
                icon={SvgBorderAll}
                onClick={onClose}
                activeMatch={isAdminSystemActive("border")}
              />
              <Nav.Item text={"H-Rail"} to="h-rail" icon={SvgH} onClick={onClose} />
            </Nav.Item>
          )}
          {!!permissions.admin.canManageItems && (
            <Nav.Item text={i18n.translation.items.plural} to="/admin/items" icon={SvgItems} onClick={onClose} />
          )}
          {!!permissions.admin.canManageClients && (
            <Nav.Item
              text={i18n.translation.clients.plural}
              to="/admin/clients"
              icon={SvgViewTimeline}
              onClick={onClose}
            />
          )}
          {!!permissions.admin.canManageUsers && (
            <Nav.Item text={i18n.translation.users.plural} to="/admin/users" icon={SvgPeople} onClick={onClose} />
          )}
        </Nav.Group>
      )}
    </Nav>
  );
};

export default MainMenu;
